import React, { useState, useEffect } from 'react';
import CreateTableForm from './CreateTableForm';
import ColumnConfig from './ColumnConfig';
import config from '../config.json';
import LanguageSelection from './LanguageSelection';
import TablesCreated from '../Component/TablesCreated';

type ToastType = 'success' | 'error' | 'warning';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface Props {
  sessionUUID: string | null;
}

const toastStyles: Record<ToastType, React.CSSProperties> = {
  success: { background: 'linear-gradient(135deg, #28a745, #20c997)' },
  error:   { background: 'linear-gradient(135deg, #dc3545, #c82333)' },
  warning: { background: 'linear-gradient(135deg, #ffc107, #e0a800)' },
};

const toastIcons: Record<ToastType, string> = {
  success: '✔',
  error:   '✖',
  warning: '⚠',
};

const Sqlcontent: React.FC<Props> = ({ sessionUUID }) => {
  const [tableName, setTableName] = useState('');
  const [columnCount, setColumnCount] = useState(1);
  const [columns, setColumns] = useState<any[]>([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [formFilled, setFormFilled] = useState(false);
  const [foreignTables, setForeignTables] = useState<string[]>([]);
  const [showModals, setShowModals] = useState(false);
  const [refreshTables, setRefreshTables] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [tableCount, setTableCount] = useState(0);

  // ── Modo edición ──────────────────────────────────────────────────────────
  const [tableBeingEdited, setTableBeingEdited] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const showToast = (message: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  };

  // ── Cuando el UUID cambia (login/logout), refrescar tablas ────────────────
  useEffect(() => {
    setRefreshTables(prev => !prev);
  }, [sessionUUID]);

  useEffect(() => {
    const savedTable = localStorage.getItem('tableName');
    const savedColumns = localStorage.getItem('columns');
    if (savedTable) setTableName(savedTable);
    if (savedColumns) {
      try {
        const parsedColumns = JSON.parse(savedColumns);
        setColumns(parsedColumns);
      } catch (e) {
        console.error('Error al parsear columnas guardadas:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (tableName) localStorage.setItem('tableName', tableName);
    if (columns.length > 0) localStorage.setItem('columns', JSON.stringify(columns));
  }, [tableName, columns]);

  const handleCreateTable = () => {
    const idColumn = {
      name: 'id', type: 'Número', isNullable: false,
      isPrimaryKey: true, isUnique: true, isForeignKey: false,
      relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT',
    };
    const newColumns = [
      idColumn,
      ...Array.from({ length: columnCount }, () => ({
        name: '', type: 'Texto', isNullable: false,
        isPrimaryKey: false, isUnique: false, isForeignKey: false,
        relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT',
      })),
    ];
    setColumns(newColumns);
    setShowColumnConfig(true);
  };

  const handleColumnChange = (index: number, key: string, value: any) => {
    const updatedColumns = [...columns];
    updatedColumns[index][key] = value;
    setColumns(updatedColumns);
    setFormFilled(updatedColumns.some((col) => col.name || col.type !== 'Texto' || col.isNullable));
    localStorage.setItem('columns', JSON.stringify(updatedColumns));
  };

  const addNewColumn = () => {
    setColumns([...columns, {
      name: '', type: 'Texto', isNullable: false,
      isPrimaryKey: false, isUnique: false, isForeignKey: false,
      relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT',
    }]);
  };

  const removeLastColumn = () => {
    if (columns.length > 0) setColumns(columns.slice(0, -1));
  };

  const handleNewTable = () => {
    setTableName(''); setColumnCount(1); setColumns([]);
    setShowColumnConfig(false); setFormFilled(false);
    setTableBeingEdited(null);
    localStorage.removeItem('tableName'); localStorage.removeItem('columns');
  };

  // ── Cargar tabla para edición ─────────────────────────────────────────────
  const handleEditTable = (name: string) => {
    const relationsKey = `omnicrud_relations_${sessionUUID}`;
    const saved = JSON.parse(localStorage.getItem(relationsKey) || '{}');
    const savedColumns = saved[name];

    if (!savedColumns || savedColumns.length === 0) {
      showToast(`No se encontraron columnas guardadas para "${name}". Recrea la tabla manualmente.`, 'warning');
      return;
    }

    const loadedColumns = savedColumns.map((c: any) => ({
      name: c.name,
      type: c.type,
      isNullable: c.isNullable ?? false,
      isPrimaryKey: c.isPrimaryKey ?? false,
      isUnique: c.isUnique ?? false,
      isForeignKey: c.isForeignKey ?? false,
      relatedTable: c.relatedTable ?? '',
      relatedColumn: c.relatedColumn ?? '',
      onDelete: c.onDelete ?? 'RESTRICT',
      onUpdate: c.onUpdate ?? 'RESTRICT',
    }));

    setTableName(name);
    setColumns(loadedColumns);
    setShowColumnConfig(true);
    setTableBeingEdited(name);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generateSQL = (nameOverride?: string) => {
    const tName = nameOverride || tableName;
    if (!tName || columns.length === 0) {
      showToast('Por favor, ingrese un nombre de tabla y al menos una columna.', 'warning');
      return null;
    }
    let sql = `CREATE TABLE ${tName} (`;
    const columnNamesSet = new Set();
    for (let idx = 0; idx < columns.length; idx++) {
      const col = columns[idx];
      if (columnNamesSet.has(col.name)) { showToast(`La columna "${col.name}" está duplicada.`, 'error'); return null; }
      columnNamesSet.add(col.name);
      let sqlType = '';
      switch (col.type) {
        case 'Texto':        sqlType = 'VARCHAR(255)'; break;
        case 'Número':       sqlType = 'INT';           break;
        case 'Fecha':        sqlType = 'DATE';          break;
        case 'Fecha y Hora': sqlType = 'DATETIME';      break;
        case 'Relacion':     sqlType = 'INT';           break;
        default:             sqlType = 'VARCHAR(255)';
      }
      sql += `${col.name} ${sqlType}`;
      if (!col.isNullable)  sql += ' NOT NULL';
      if (col.isPrimaryKey) sql += ' AUTO_INCREMENT PRIMARY KEY';
      if (col.isUnique)     sql += ' UNIQUE';
      if (idx < columns.length - 1) sql += ', ';
    }
    sql += ');';
    return sql;
  };

  // ── Crear tabla normal ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const sql = generateSQL();
    if (!sql) return;
    if (!sessionUUID) { showToast('No se encontró el UUID de la base de datos.', 'error'); return; }
    try {
      const response = await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createTable',
          databaseName: sessionUUID,
          tableName,
          columns: columns.map((c) => [c.name, c.type]),
          sqlQuery: sql,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        const relationsKey = `omnicrud_relations_${sessionUUID}`;
        const existing = JSON.parse(localStorage.getItem(relationsKey) || '{}');
        existing[tableName] = columns.map((c) => ({
          name: c.name, type: c.type,
          isPrimaryKey: c.isPrimaryKey || false,
          isNullable: c.isNullable || false,
          isUnique: c.isUnique || false,
          isForeignKey: c.isForeignKey || false,
          relatedTable: c.relatedTable || '',
        }));
        localStorage.setItem(relationsKey, JSON.stringify(existing));
        showToast(`Tabla '${tableName}' creada con éxito.`, 'success');
        setRefreshTables(prev => !prev);
        handleNewTable();
      } else {
        showToast(result.message || 'No se pudo crear la tabla.', 'error');
      }
    } catch { showToast('Hubo un error al ejecutar la sentencia SQL.', 'error'); }
  };

  // ── Actualizar tabla (modo edición) ───────────────────────────────────────
  const handleUpdateTable = async () => {
    if (!tableBeingEdited || !sessionUUID) return;
    const sql = generateSQL(tableBeingEdited);
    if (!sql) return;

    setIsUpdating(true);
    try {
      const dropSql = `DROP TABLE IF EXISTS ${tableBeingEdited};`;
      const dropRes = await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createTable',
          databaseName: sessionUUID,
          tableName: tableBeingEdited,
          columns: columns.map((c) => [c.name, c.type]),
          sqlQuery: dropSql + ' ' + sql,
        }),
      });
      const dropResult = await dropRes.json();
      if (!dropRes.ok) {
        showToast(dropResult.message || 'Error al actualizar la tabla.', 'error');
        setIsUpdating(false);
        return;
      }

      const relationsKey = `omnicrud_relations_${sessionUUID}`;
      const existing = JSON.parse(localStorage.getItem(relationsKey) || '{}');
      existing[tableBeingEdited] = columns.map((c) => ({
        name: c.name, type: c.type,
        isPrimaryKey: c.isPrimaryKey || false,
        isNullable: c.isNullable || false,
        isUnique: c.isUnique || false,
        isForeignKey: c.isForeignKey || false,
        relatedTable: c.relatedTable || '',
      }));
      localStorage.setItem(relationsKey, JSON.stringify(existing));

      const projectConfig = JSON.parse(localStorage.getItem('projectConfig') || '{}');
      if (projectConfig?.framework && projectConfig?.sessionUUID) {
        showToast('Tabla actualizada. Regenerando módulo del proyecto...', 'success');
        try {
          await fetch(`${config.ENDPOINTS.CALLER_LANGUAGE}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'updateModule',
              tableToUpdate: tableBeingEdited,
              columns: existing[tableBeingEdited],
              framework: projectConfig.framework,
              databaseName: projectConfig.sessionUUID,
            }),
          });
          showToast(`Módulo "${tableBeingEdited}" regenerado con éxito.`, 'success');
        } catch (e) {
          console.error('Error al regenerar módulo:', e);
          showToast('Tabla actualizada, pero hubo un error al regenerar el módulo.', 'warning');
        }
      } else {
        showToast(`Tabla "${tableBeingEdited}" actualizada con éxito.`, 'success');
      }

      setRefreshTables(prev => !prev);
      handleNewTable();
    } catch {
      showToast('Hubo un error al actualizar la tabla.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const callBackendForGeneration = async (framework = 'Angular', backendType = 'spring') => {
    try {
      const tablesResponse = await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listTables', databaseName: sessionUUID }),
      });
      const tablesData = await tablesResponse.json();
      const allTableNames: string[] = tablesData.tables || [];
      if (allTableNames.length === 0) { showToast('No hay tablas creadas para generar el proyecto.', 'warning'); return; }

      const relationsKey = `omnicrud_relations_${sessionUUID}`;
      const savedRelations = JSON.parse(localStorage.getItem(relationsKey) || '{}');

      const allTablesWithColumns = await Promise.all(
        allTableNames.map(async (table) => {
          if (savedRelations[table]) {
            return { tableName: table, columns: savedRelations[table] };
          }
          const colResponse = await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'listColumns', databaseName: sessionUUID, tableName: table }),
          });
          const colData = await colResponse.json();
          return { tableName: table, columns: colData.columns || [] };
        })
      );

      const response = await fetch(`${config.ENDPOINTS.CALLER_LANGUAGE}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generateCode', framework, backendType, tables: allTablesWithColumns, databaseName: sessionUUID }),
      });
      const result = await response.json();
      if (!response.ok) console.error(`Error al generar ${framework}:`, result.message);
    } catch (error) { console.error('Error al generar el código:', error); }
  };

  const actionBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '7px 16px', fontSize: '13px', fontWeight: 500,
    borderRadius: '8px', border: 'none', cursor: 'pointer',
    transition: 'all 0.15s ease', whiteSpace: 'nowrap',
  };

  return (
    <div>
      {/* TOASTS */}
      <div style={{ position: 'fixed', top: '70px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px' }}>    
          {toasts.map((toast) => (
          <div key={toast.id} style={{
            ...toastStyles[toast.type],
            color: toast.type === 'warning' ? '#333' : '#fff',
            padding: '14px 20px', borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: '10px',
            minWidth: '280px', maxWidth: '380px',
            fontSize: '14px', fontWeight: 500,
            animation: 'slideIn 0.3s ease',
          }}>
            <span style={{ fontSize: '18px' }}>{toastIcons[toast.type]}</span>
            <span>{toast.message}</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
        .action-btn:hover { filter: brightness(0.92); transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
        .action-btn:active { transform: translateY(0); }
      `}</style>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {tableBeingEdited ? `Editando tabla: ${tableBeingEdited}` : 'Crear nueva tabla'}
          </h3>
        </div>
        <div className="card-body">
          {!showColumnConfig && (
            <CreateTableForm
              tableName={tableName} columnCount={columnCount}
              setTableName={setTableName} setColumnCount={setColumnCount}
              handleCreateTable={handleCreateTable}
              handleNext={() => setShowModals(true)}
              hasTables={tableCount > 0}
            />
          )}

          {showColumnConfig && (
            <div className="mt-4">
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '16px' }}>
                Configurar columnas
              </h4>
              <form>
                {columns.map((col, index) => (
                  <ColumnConfig key={index} index={index} column={col}
                    handleColumnChange={handleColumnChange} foreignTables={foreignTables} />
                ))}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
                  <button type="button" className="action-btn"
                    style={{ ...actionBtn, background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}
                    onClick={addNewColumn}>
                    ＋ Agregar columna
                  </button>
                  <button type="button" className="action-btn"
                    style={{ ...actionBtn, background: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a' }}
                    onClick={removeLastColumn}>
                    － Quitar última
                  </button>

                  {tableBeingEdited ? (
                    <button type="button" className="action-btn"
                      style={{ ...actionBtn, background: '#1a7fd4', color: '#fff', border: '1px solid #1569b5', opacity: isUpdating ? 0.7 : 1 }}
                      onClick={handleUpdateTable}
                      disabled={isUpdating}>
                      {isUpdating ? '⏳ Actualizando...' : '💾 Actualizar tabla'}
                    </button>
                  ) : (
                    <button type="button" className="action-btn"
                      style={{ ...actionBtn, background: '#1a7fd4', color: '#fff', border: '1px solid #1569b5' }}
                      onClick={handleSubmit}>
                      ✔ Crear tabla
                    </button>
                  )}

                  <button type="button" className="action-btn"
                    style={{ ...actionBtn, background: '#f5f5f5', color: '#555', border: '1px solid #ddd' }}
                    onClick={handleNewTable}>
                    ← {tableBeingEdited ? 'Cancelar edición' : 'Volver'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <LanguageSelection
          sessionUUID={sessionUUID}
          show={showModals}
          onClose={() => setShowModals(false)}
          tableName={tableName}
          columns={columns}
          onGenerate={callBackendForGeneration}
        />
        <TablesCreated
          sessionUUID={sessionUUID}
          refresh={refreshTables}
          onTablesLoaded={(count, tables) => {
            setTableCount(count);
            setForeignTables(tables);
          }}
          onEditTable={handleEditTable}
        />
      </div>
    </div>
  );
};

export default Sqlcontent;